/**
 * The shell for CPA as a BD-facing app, at `/app/cpa`.
 *
 * ## Why its own shell rather than the admin one
 *
 * `CpaLayout` is the admin surface: Cloudscape `ContentLayout`, a breadcrumb reading
 * "Rocky > Rocky Backend Management System > CPA", and a tab bar. None of that is right here.
 * The audience is the account team, not Deal Desk; the breadcrumb would be wrong rather than
 * merely unnecessary; and there are no tabs, because this app is one task.
 *
 * ## It owns no chat panel
 *
 * There was one here, and it was a mistake: the portal shell already keeps a single
 * `<ChatPanel />` mounted for every main-app route (see `ChatShell` in App.tsx), so this was a
 * second panel for the same purpose. Two drawers side by side left the user guessing which
 * button opened which, and only one of them could actually talk to the agent.
 *
 * So `*NOTES` now sends the note to the REAL chat through `useChatPanel().sendPrompt`, which
 * opens the panel and fires a request. That is also closer to the design, where the note
 * arrives as a message in the conversation rather than in a panel of its own.
 */
import { useNavigate } from 'react-router-dom';

import './CpaAppLayout.css';
import QuestionnairePage from './QuestionnairePage';

const ACTIONS = [
  { id: 'chatbot', label: 'CHATBOT' },
  { id: 'contact', label: 'CONTACT' },
  { id: 'new', label: 'NEW' },
  { id: 'history', label: 'HISTORY' },
] as const;

function ActionIcon({ id }: { id: (typeof ACTIONS)[number]['id'] }) {
  // Inline so the bar has no icon-font or sprite dependency; four small glyphs is less
  // machinery than wiring an icon set for one screen.
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const };
  switch (id) {
    case 'chatbot':
      return (
        <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
          <rect x="2.5" y="4" width="13" height="9" rx="2" {...common} />
          <path d="M6 16l2-3M12 16l-2-3" {...common} />
          <circle cx="7" cy="8.5" r="1" fill="currentColor" />
          <circle cx="11" cy="8.5" r="1" fill="currentColor" />
        </svg>
      );
    case 'contact':
      return (
        <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
          <rect x="2" y="4" width="14" height="10" rx="1.5" {...common} />
          <path d="M2.6 5l6.4 5 6.4-5" {...common} />
        </svg>
      );
    case 'new':
      return (
        <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
          <path d="M9 3.5v11M3.5 9h11" {...common} />
        </svg>
      );
    case 'history':
      return (
        <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
          <path d="M3 5h12M3 9h12M3 13h8" {...common} />
        </svg>
      );
  }
}

export default function CpaAppLayout() {
  const navigate = useNavigate();

  return (
    <div className="cpa-app">
      {/*
        The top bar.
        Sticky rather than scrolling away: the questionnaire is a long single column, and the
        back link is the only way out of the app.
      */}
      <header className="cpa-app__bar">
        <button
          type="button"
          className="cpa-app__back"
          onClick={() => navigate('/')}
          aria-label="返回 Rocky"
        >
          <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
            <path
              d="M10 2.5L4.5 8l5.5 5.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Rocky</span>
        </button>

        <nav className="cpa-app__actions">
          {ACTIONS.map((a) => (
            <button
              type="button"
              key={a.id}
              className="cpa-app__action"
              // No handler, and disabled rather than silently inert.
              //
              // Each of these belongs to a different existing surface -- the agent chat, the
              // contact list, a new session, session history -- and none is wired yet. Wiring
              // them to something plausible would be a guess that looks like a decision, and
              // a button that visibly does nothing when clicked is worse than one that says
              // it is not available.
              disabled
            >
              <ActionIcon id={a.id} />
              <span>{a.label}</span>
            </button>
          ))}
        </nav>

        {/*
          No chat toggle here: the portal shell already has one at the top right, and a second
          button for the same drawer would leave the user guessing which panel each one opens.
          This drawer opens from `*NOTES` on a question and closes from its own × .
        */}
      </header>

      {/*
        One content column, and no drawer. The portal's own ChatPanel sits to the right of this
        whole route, placed by ChatShell -- see the note at the top of this file.
      */}
      <div className="cpa-app__body">
        <QuestionnairePage />
      </div>
    </div>
  );
}
